import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, User, Tag } from 'lucide-react';
import '../../styles/realsignal-theme.css';

interface OnboardingPageProps {
    onCreateAccount?: (data: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        betaCode?: string;
    }) => void;
    onGoogleSignUp?: () => void;
    onSignIn?: () => void;
}

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    backgroundWell: '#0A1218',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
};

export const OnboardingPage: React.FC<OnboardingPageProps> = ({
    onCreateAccount,
    onGoogleSignUp,
    onSignIn,
}) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [betaCode, setBetaCode] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            return;
        }
        onCreateAccount?.({
            firstName,
            lastName,
            email,
            password,
            betaCode: betaCode || undefined,
        });
    };

    const passwordsMatch = password === confirmPassword || confirmPassword === '';
    const passwordLongEnough = password.length >= 8 || password === '';

    return (
        <div
            className="min-h-screen flex items-center justify-center relative overflow-hidden"
            style={{
                background: colors.background,
                fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
        >
            {/* Texture overlay - grainy noise blended with navy background */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'url(/assets/realsignal-bg-texture.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.12,
                    mixBlendMode: 'luminosity',
                    filter: 'grayscale(100%)',
                    pointerEvents: 'none',
                }}
            />
            <div className="relative z-10 w-full max-w-md mx-auto px-6 py-8">
                {/* Logo and tagline */}
                <div className="text-center mb-8">
                    <div className="mb-6">
                        <div className="flex justify-center mb-4">
                            <img
                                src="/assets/realsignal-logo.png"
                                alt="RealSignal"
                                className="h-12 w-auto"
                                style={{
                                    filter: 'brightness(1.15)',
                                }}
                            />
                        </div>
                        <p style={{ color: colors.textSecondary, fontSize: '1.125rem' }}>
                            Real Estate Distress Intelligence
                        </p>
                    </div>
                </div>

                {/* Onboarding Card */}
                <div
                    style={{
                        background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundLight} 100%)`,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '2rem',
                        padding: '2rem',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    }}
                >
                    <div className="text-center mb-6">
                        <h2
                            style={{
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                color: colors.textPrimary,
                                marginBottom: '0.5rem',
                            }}
                        >
                            Create Account
                        </h2>
                        <p style={{ color: colors.textSecondary }}>
                            Start tracking distressed properties today
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Name Fields - Side by Side */}
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            {/* First Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: colors.textSecondary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    First Name
                                </label>
                                <div className="relative">
                                    <User
                                        style={{
                                            position: 'absolute',
                                            left: '0.75rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: colors.textMuted,
                                            width: '1.25rem',
                                            height: '1.25rem',
                                            zIndex: 10,
                                        }}
                                    />
                                    <input
                                        type="text"
                                        name="firstName"
                                        placeholder="John"
                                        value={firstName}
                                        onChange={e => setFirstName(e.target.value)}
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${colors.border}`,
                                            borderRadius: '0.75rem',
                                            padding: '0.75rem 1rem 0.75rem 2.75rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Last Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: colors.textSecondary,
                                        marginBottom: '0.5rem',
                                    }}
                                >
                                    Last Name
                                </label>
                                <div className="relative">
                                    <User
                                        style={{
                                            position: 'absolute',
                                            left: '0.75rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: colors.textMuted,
                                            width: '1.25rem',
                                            height: '1.25rem',
                                            zIndex: 10,
                                        }}
                                    />
                                    <input
                                        type="text"
                                        name="lastName"
                                        placeholder="Doe"
                                        value={lastName}
                                        onChange={e => setLastName(e.target.value)}
                                        style={{
                                            width: '100%',
                                            background: colors.background,
                                            border: `1px solid ${colors.border}`,
                                            borderRadius: '0.75rem',
                                            padding: '0.75rem 1rem 0.75rem 2.75rem',
                                            color: colors.textPrimary,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Email Field */}
                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: colors.textSecondary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail
                                    style={{
                                        position: 'absolute',
                                        left: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        zIndex: 10,
                                    }}
                                />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="investor@company.com"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '0.75rem',
                                        padding: '0.75rem 1rem 0.75rem 2.75rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: colors.textSecondary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Password
                            </label>
                            <div className="relative">
                                <Lock
                                    style={{
                                        position: 'absolute',
                                        left: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        zIndex: 10,
                                    }}
                                />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    placeholder="Min. 8 characters"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${!passwordLongEnough ? '#EF4444' : colors.border}`,
                                        borderRadius: '0.75rem',
                                        padding: '0.75rem 3rem 0.75rem 2.75rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        zIndex: 10,
                                    }}
                                >
                                    {showPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            {!passwordLongEnough && (
                                <p
                                    style={{
                                        fontSize: '0.75rem',
                                        color: '#EF4444',
                                        marginTop: '0.25rem',
                                    }}
                                >
                                    Password must be at least 8 characters
                                </p>
                            )}
                        </div>

                        {/* Confirm Password Field */}
                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: colors.textSecondary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock
                                    style={{
                                        position: 'absolute',
                                        left: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        zIndex: 10,
                                    }}
                                />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    placeholder="Confirm your password"
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${!passwordsMatch ? '#EF4444' : colors.border}`,
                                        borderRadius: '0.75rem',
                                        padding: '0.75rem 3rem 0.75rem 2.75rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        zIndex: 10,
                                    }}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="w-5 h-5" />
                                    ) : (
                                        <Eye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            {!passwordsMatch && (
                                <p
                                    style={{
                                        fontSize: '0.75rem',
                                        color: '#EF4444',
                                        marginTop: '0.25rem',
                                    }}
                                >
                                    Passwords do not match
                                </p>
                            )}
                        </div>

                        {/* Beta Code Field */}
                        <div>
                            <label
                                style={{
                                    display: 'block',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: colors.textSecondary,
                                    marginBottom: '0.5rem',
                                }}
                            >
                                Beta Code{' '}
                                <span style={{ color: colors.textMuted, fontWeight: 400 }}>
                                    (optional)
                                </span>
                            </label>
                            <div className="relative">
                                <Tag
                                    style={{
                                        position: 'absolute',
                                        left: '0.75rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: colors.textMuted,
                                        width: '1.25rem',
                                        height: '1.25rem',
                                        zIndex: 10,
                                    }}
                                />
                                <input
                                    type="text"
                                    name="betaCode"
                                    placeholder="Enter beta access code"
                                    value={betaCode}
                                    onChange={e => setBetaCode(e.target.value.toUpperCase())}
                                    style={{
                                        width: '100%',
                                        background: colors.background,
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: '0.75rem',
                                        padding: '0.75rem 1rem 0.75rem 2.75rem',
                                        color: colors.textPrimary,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                    }}
                                />
                            </div>
                            <p
                                style={{
                                    fontSize: '0.75rem',
                                    color: colors.textMuted,
                                    marginTop: '0.25rem',
                                }}
                            >
                                Have a beta code? Enter it for early access pricing.
                            </p>
                        </div>

                        {/* Pricing Display */}
                        <div
                            style={{
                                background: colors.backgroundWell,
                                borderRadius: '1rem',
                                padding: '1rem',
                                border: `1px solid ${colors.border}`,
                                textAlign: 'center',
                            }}
                        >
                            <p
                                style={{
                                    fontSize: '0.75rem',
                                    color: colors.textMuted,
                                    marginBottom: '0.25rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                Subscription
                            </p>
                            <p
                                style={{
                                    fontSize: '1.5rem',
                                    fontWeight: 700,
                                    color: colors.textPrimary,
                                }}
                            >
                                $149.99
                                <span
                                    style={{
                                        fontSize: '0.875rem',
                                        fontWeight: 400,
                                        color: colors.textSecondary,
                                    }}
                                >
                                    {' '}
                                    / user / month
                                </span>
                            </p>
                        </div>

                        {/* Create Account Button */}
                        <button
                            type="submit"
                            style={{
                                width: '100%',
                                background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                                color: 'white',
                                fontWeight: 600,
                                padding: '1rem 1.5rem',
                                borderRadius: '1.5rem',
                                border: `2px solid ${colors.accent}`,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                boxShadow:
                                    '0 10px 24px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                                marginTop: '1.5rem',
                            }}
                        >
                            <span>Create Account</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </form>

                    {/* Sign In Link */}
                    <div className="mt-6 text-center">
                        <p style={{ color: colors.textSecondary }}>
                            Already have an account?
                            <button
                                onClick={onSignIn}
                                style={{
                                    marginLeft: '0.5rem',
                                    color: colors.accentLight,
                                    fontWeight: 500,
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                Sign In
                            </button>
                        </p>
                    </div>

                    {/* Divider */}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div
                                    style={{
                                        width: '100%',
                                        borderTop: `1px solid ${colors.border}`,
                                    }}
                                />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span
                                    style={{
                                        padding: '0 0.5rem',
                                        background: colors.backgroundLight,
                                        color: colors.textSecondary,
                                    }}
                                >
                                    Or continue with
                                </span>
                            </div>
                        </div>

                        {/* Social Login Icons */}
                        <div className="mt-6 flex justify-center gap-4">
                            {/* Google */}
                            <button
                                title="Sign up with Google"
                                onClick={onGoogleSignUp}
                                style={{
                                    background: colors.background,
                                    color: colors.textSecondary,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '50%',
                                    width: '48px',
                                    height: '48px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                    />
                                </svg>
                            </button>
                            {/* Facebook */}
                            <button
                                title="Sign up with Facebook"
                                style={{
                                    background: colors.background,
                                    color: colors.textSecondary,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '50%',
                                    width: '48px',
                                    height: '48px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </button>
                            {/* Apple */}
                            <button
                                title="Sign up with Apple"
                                style={{
                                    background: colors.background,
                                    color: colors.textSecondary,
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: '50%',
                                    width: '48px',
                                    height: '48px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <p
                    style={{
                        marginTop: '1.5rem',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        color: colors.textMuted,
                    }}
                >
                    By continuing, you agree to our{' '}
                    <button
                        style={{
                            color: colors.accentLight,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        Terms of Service
                    </button>{' '}
                    and{' '}
                    <button
                        style={{
                            color: colors.accentLight,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        Privacy Policy
                    </button>
                </p>
            </div>
        </div>
    );
};

export default OnboardingPage;
